import axios from 'axios'
import type { AxiosError, AxiosRequestConfig, AxiosResponse, AxiosRequestHeaders } from 'axios'
import { getDefaultAuthProvider } from '@lib/auth/AuthProvider'

declare module 'axios' {
  // Allow metadata on requests(used for debugging/extension)
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  export interface AxiosRequestConfig {
    metadata?: object
  }
}

export type RequestConfig<TVariables = unknown> = {
  method: 'get' | 'put' | 'patch' | 'post' | 'delete'
  url: string
  params?: unknown
  data?: TVariables
  responseType?: 'arraybuffer' | 'blob' | 'document' | 'json' | 'text' | 'stream'
  signal?: AbortSignal
  headers?: AxiosRequestConfig['headers']
}

export type ResponseConfig<TData = unknown> = {
  data: TData
  status: number
  statusText: string
  headers?: AxiosResponse['headers']
}

export const axiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_ENDPOINT || '',
  headers: { 'Content-Type': 'application/json' },
  timeout: 60000
})

// Inject Authorization header when available
axiosInstance.interceptors.request.use(
  async (config) => {
    const headers: AxiosRequestHeaders = (config.headers ?? {}) as AxiosRequestHeaders
    const allHeaders = getDefaultAuthProvider().getHeaders()

    if (allHeaders && typeof allHeaders === 'object') {
      for (const [key, value] of Object.entries(allHeaders as Record<string, unknown>)) {
        if (!value) continue
        ;(headers as any)[key] = value as any
      }
    }

    config.headers = headers

    return config
  },
  (error) => Promise.reject(error)
)

// On 401 redirect to login (no refresh flow for PCC)
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    if (error.response?.status === 401) {
      const authProvider = getDefaultAuthProvider()
      // Clear the session
      authProvider.logout()

      // Redirect to login page
      // For email auth, redirect to root (login page)
      // For PCC auth, redirect to the auth provider endpoint
      if (typeof window !== 'undefined') {
        // Small delay to ensure logout is completed
        setTimeout(() => {
          authProvider.login()
        }, 100)
      }
    }
    return Promise.reject(error)
  }
)

export const axiosClient = async <TData, TError = unknown, TVariables = unknown>(
  config: RequestConfig<TVariables>
): Promise<ResponseConfig<TData>> => {
  try {
    const res = await axiosInstance.request<TData, AxiosResponse<TData>>(config as AxiosRequestConfig)
    return {
      data: res.data,
      status: res.status,
      statusText: res.statusText,
      headers: res.headers
    }
  } catch (e) {
    throw e as AxiosError<TError>
  }
}

export default axiosClient
